import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { sessionGuard } from 'app/core/auth/guards/session.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed-in user to the '/user'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'code-verification',
                canActivate: [sessionGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/code-verification/code-verification.routes'
                    ),
            },
            {
                path: 'reset-password',
                canActivate: [sessionGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
            {
                path: '2fa_auth',
                canActivate: [sessionGuard],
                loadChildren: () =>
                    import(
                        'app/modules/auth/two-factor-verification/two-factor-verification.routes'
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'cms-page-management',
                loadChildren: () =>
                    import(
                        'app/modules/admin/cms-page-management/cms-page-management.routes'
                    ),
            },
            {
                path: 'template',
                loadChildren: () =>
                    import('app/modules/admin/template/template.routes'),
            },
            {
                path: 'users',
                loadChildren: () =>
                    import('app/modules/admin/users/users.routes'),
            },
            {
                path: 'dashboard',
                loadChildren: () =>
                    import('app/modules/admin/dashboard/dashboard.routes'),
            },
            {
                path: 'settings',
                loadChildren: () =>
                    import('app/modules/admin/settings/settings.routes'),
            },
            {
                path: 'vehicle-make',
                loadChildren: () =>
                    import(
                        'app/modules/admin/vehicle-make/vehicle-make.routes'
                    ),
            },
            {
                path: 'vehicle-model',
                loadChildren: () =>
                    import(
                        'app/modules/admin/vehicle-model/vehicle-model.routes'
                    ),
            },
            {
                path: 'customer-source',
                loadChildren: () =>
                    import(
                        'app/modules/admin/customer-source/customer-source.routes'
                    ),
            },
            {
                path: 'inventory-source',
                loadChildren: () =>
                    import(
                        'app/modules/admin/inventory-source/inventory-source.routes'
                    ),
            },
            {
                path: 'inventory',
                loadChildren: () =>
                    import('app/modules/admin/inventory/inventory.routes'),
            },
            {
                path: 'customer',
                loadChildren: () =>
                    import('app/modules/admin/customer/customer.routes'),
            },
            {
                path: 'permission',
                loadChildren: () =>
                    import('app/modules/admin/permission/permission.routes'),
            },
            {
                path: 'designation',
                loadChildren: () =>
                    import('app/modules/admin/designation/designation.routes'),
            },
            {
                path: 'sales',
                loadChildren: () =>
                    import('app/modules/admin/sales/sales.routes'),
            },
            {
                path: 'map-view',
                loadChildren: () =>
                    import('app/modules/admin/map-view/map-view.routes'),
            },
            {
                path: 'current-inventory',
                loadChildren: () =>
                    import(
                        'app/modules/admin/inventory/current-inventory/current-inventory.routes'
                    ),
            },
            {
                path: 'lead',
                loadChildren: () =>
                    import('app/modules/admin/lead/lead.routes'),
            },
            {
                path: 'document-template',
                loadChildren: () =>
                    import(
                        'app/modules/admin/document-template/document-template.routes'
                    ),
            },
            {
                path: 'documents',
                loadChildren: () =>
                    import('app/modules/admin/documents/documents.routes'),
            },
            {
                path: 'bookmarks',
                loadChildren: () =>
                    import('app/modules/admin/bookmarks/bookmarks.routes'),
            },
            {
                path: 'document-category',
                loadChildren: () =>
                    import(
                        'app/modules/admin/document-category/document-category.routes'
                    ),
            },
            {
                path: 'seller',
                loadChildren: () =>
                    import('app/modules/admin/seller/seller.routes'),
            },
            {
                path: 'purchase-lead',
                loadChildren: () =>
                    import(
                        'app/modules/admin/purchase-lead/purchase-lead.routes'
                    ),
            },
            {
                path: 'finder',
                loadChildren: () =>
                    import('app/modules/admin/finder/finder.routes'),
            },
            {
                path: 'inspection',
                loadChildren: () =>
                    import('app/modules/admin/inspection/inspection.routes'),
            },
        ],
    },
];
